'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';

type CalcState = {
  emp: number;
  current: number;
  mod_rpo: boolean;
  mod_payroll: boolean;
  mod_helpdesk: boolean;
  mod_ld: boolean;
  mod_edo: boolean;
  vac: number;
  events: number;
  sla: boolean;
  iso: boolean;
  discount: boolean;
};

const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

function useCalc(defaults: CalcState) {
  const [s, setS] = React.useState<CalcState>(() => {
    if (typeof window === 'undefined') {
      return defaults;
    }
    try {
      const raw = localStorage.getItem('outsourcing_calc');
      return raw
        ? { ...defaults, ...JSON.parse(raw) }
        : defaults;
    } catch {
      return defaults;
    }
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('outsourcing_calc', JSON.stringify(s));
    }
  }, [s]);

  return { s, setS };
}

function Section({
  id, gray, className, children,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={`py-20 box-border ${gray ? 'bg-slate-50' : ''} ${className ?? ''}`}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return <div className={`rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${className ?? ''}`}>{children}</div>;
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px]">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base">{icon}</div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  const { label, value, onChange, min, step = 1 } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function LabeledCheckbox({
  label, checked, onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
      />
      {label}
    </label>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B]">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

const getStatusClasses = (status: string) => {
  if (status.startsWith('Ошибка') || status.includes('сети')) {
    return 'mt-2 text-sm font-medium text-red-600';
  }
  if (status.startsWith('Пожалуйста')) {
    return 'mt-2 text-sm font-medium text-amber-600';
  }
  if (status.includes('успешно')) {
    return 'mt-2 text-sm font-medium text-green-600';
  }
  return 'mt-2 text-sm text-slate-500';
};

export default function OutsourcingFunctionsPage() {
  const defaults = {
    emp: 300, current: 900_000, mod_rpo: true, mod_payroll: true, mod_helpdesk: true, mod_ld: false, mod_edo: true,
    vac: 15, events: 200, sla: true, iso: false, discount: false,
  };
  const { s, setS } = useCalc(defaults);

  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    processes: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgreementChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, agreed: checked }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      setFormStatus('Произошла ошибка: имя, телефон и email не могут быть пустыми.');
      return;
    }

    setFormStatus('Отправка...');

    const { agreed, ...dataToSend } = formData;

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/outsourcing-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...dataToSend,
            formName: 'Аутсорсинг функций', 
          },
        }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', processes: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setFormStatus(`Произошла ошибка: ${errorMessage}`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  }

    let service = 0;
    if (s.mod_rpo) service += 120_000 + s.vac * 18_000;
    if (s.mod_payroll) service += s.emp * 180 + s.events * 150;
    if (s.mod_helpdesk) service += s.emp * 80;
    if (s.mod_ld) service += Math.ceil(s.emp / 100) * 25_000;
    if (s.mod_edo) service += 30_000;

    if (s.sla) service = Math.round(service * 1.10);
    if (s.iso) service = Math.round(service * 1.05);
    if (s.discount) service = Math.round(service * 0.93);

    const saving = Math.max(0, s.current - service);
    const savingPct = s.current > 0 ? Math.round((saving / s.current) * 100) : 0;

    return (
      <main className="bg-white text-slate-900">
        <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20 box-border">
          <div className="mx-auto max-w-6xl pl-1 pr-6">
            <h1 className="text-4xl font-black md:text-5xl">Аутсорсинг функций</h1>
            <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95 leading-tight">
              Передаём HR-процессы под управление нашей команды: <b>RPO</b> (подбор как сервис), <b>Payroll</b>, <b>КДП</b>,
              <b>Helpdesk HR</b>, <b>L&amp;D-администрирование</b>, <b>ЭДО</b>. Работа по <b>SLA</b>, прозрачная отчётность,
              аналитика и соответствие ТК РФ/152-ФЗ.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
              >
                <a href="#calc" className="flex items-center justify-center h-full">
                  Рассчитать пакет
                </a>
              </Button>
              <Button
                asChild
                className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white"
              >
                <a href="#scope" className="flex items-center justify-center h-full">
                  Каталог услуг
                </a>
              </Button>
            </div>
            <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KPI icon={<span>📅</span>} value="4–8 недель" label="переход и запуск" />
                <KPI icon={<span>✉️</span>} value="95–98%" label="тикетов в срок SLA" />
                <KPI icon={<span>💰</span>} value="−15–30%" label="снижение HR-OPEX" />
                <KPI icon={<span>🔒</span>} value="ТК РФ / 152-ФЗ" label="комплаенс и контроль" />
              </div>
            </div>
          </div>
        </section>

        <Section id="scope" gray>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <Card>
              <h3 className="text-xl font-extrabold">RPO — подбор как сервис</h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
                <li>Управление воронкой кандидатов, поиск и отбор</li>
                <li>Интервью, оффер-менеджмент, адаптация 30/60/90</li>
                <li>Еженед. отчётность: воронка, конверсии, риски</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">Payroll / КДП</h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
                <li>Расчёт ЗП, отпуска, больничные, НДФЛ/взносы</li>
                <li>Приказы, справки, личные карточки, архив</li>
                <li>Сверка с 1С:ЗУП, контроль изменений</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">Helpdesk HR / L&amp;D</h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
                <li>Приём и решение заявок сотрудников</li>
                <li>Администрирование обучения, расписание/зачёты</li>
                <li>База знаний и FAQ</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">ЭДО и интеграции</h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
                <li>КЭП, ЭДО (Диадок/СБИС и др.)</li>
                <li>Интеграции 1С:ЗУП/СКУД/портал</li>
                <li>Поиск, контроль версий, аудит</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">Аналитика и отчётность</h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
                <li>Панели мониторинга: оборот, время работы, стоимость найма</li>
                <li>Метрики SLA: первое реагирование, решение, список задач</li>
                <li>Оценка качества, план улучшений</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">Комплаенс</h3>
              <p className="mt-3 text-base text-black">
                Договор оказания услуг, Аутсорсинг бизнес-процессов (BPO), чёткие границы ответственности. Нет «заёмного труда»: сотрудники INTERIM не под
                прямым управлением заказчика.
              </p>
            </Card>
          </div>
        </Section>

        <Section id="calc">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card>
              <h3 className="text-xl font-extrabold">Калькулятор пакета</h3>
              <p className="mt-1 text-xs text-slate-500">Фикс за модуль + транзакции + интеграции. Сравните с текущими расходами.</p>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <LabeledInputNumber label="Численность, чел." value={s.emp} onChange={(v) => setS({ ...s, emp: v })} min={1} />
                <LabeledInputNumber label="Текущие расходы HR, ₽/мес" value={s.current} onChange={(v) => setS({ ...s, current: v })} min={0} />
              </div>

              <div className="mt-3 flex flex-wrap gap-3">
                <LabeledCheckbox label="RPO" checked={s.mod_rpo} onChange={(v) => setS({ ...s, mod_rpo: v })} />
                <LabeledCheckbox label="Расчет ЗП/КДП" checked={s.mod_payroll} onChange={(v) => setS({ ...s, mod_payroll: v })} />
                <LabeledCheckbox label="Helpdesk HR" checked={s.mod_helpdesk} onChange={(v) => setS({ ...s, mod_helpdesk: v })} />
                <LabeledCheckbox label="L&D-администрирование" checked={s.mod_ld} onChange={(v) => setS({ ...s, mod_ld: v })} />
                <LabeledCheckbox label="ЭДО/КЭП" checked={s.mod_edo} onChange={(v) => setS({ ...s, mod_edo: v })} />
              </div>

              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <LabeledInputNumber label="Вакансий/мес (RPO)" value={s.vac} onChange={(v) => setS({ ...s, vac: v })} min={0} />
                <LabeledInputNumber label="Событий/мес (КДП)" value={s.events} onChange={(v) => setS({ ...s, events: v })} min={0} />
              </div>

              <div className="mt-3 flex flex-wrap gap-3">
                <LabeledCheckbox label="SLA Premium (+10%)" checked={s.sla} onChange={(v) => setS({ ...s, sla: v })} />
                <LabeledCheckbox label="Доп. безопасность (+5%)" checked={s.iso} onChange={(v) => setS({ ...s, iso: v })} />
                <LabeledCheckbox label="Годовой контракт (−7%)" checked={s.discount} onChange={(v) => setS({ ...s, discount: v })} />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <StatCard label="Текущие расходы" value={cf.format(s.current)} />
                <StatCard label="Пакет INTERIM" value={cf.format(service)} />
                <StatCard label="Экономия, ₽/мес" value={cf.format(saving)} />
                <StatCard label="Экономия, %" value={`${savingPct}%`} />
              </div>
              <p className="mt-2 text-xs text-slate-500">Расчёт ориентировочный. Итог фиксируем в SOW/договоре.</p>
            </Card>

            <Card>
              <h3 className="text-xl font-extrabold">Соглашение об уровне сервиса (SLA)</h3>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full border-collapse text-base">
                  <thead>
                    <tr className="text-left text-black">
                      <th className="border-b border-slate-200 px-2 py-2 font-bold">Процесс</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-bold">Первый ответ</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-bold">Решение</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-bold">Цель, %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-b border-slate-100 px-2 py-2">Поддержка HR</td>
                      <td className="border-b border-slate-100 px-2 py-2">≤ 30 мин</td>
                      <td className="border-b border-slate-100 px-2 py-2">Приоритет - 4 часа, 1 р.д, 3 р.д.</td>
                      <td className="border-b border-slate-100 px-2 py-2">95–98%</td>
                    </tr>
                    <tr>
                      <td className="border-b border-slate-100 px-2 py-2">КДП/ЭДО</td>
                      <td className="border-b border-slate-100 px-2 py-2">в день запроса</td>
                      <td className="border-b border-slate-100 px-2 py-2">ежедневно по регламенту</td>
                      <td className="border-b border-slate-100 px-2 py-2">98%</td>
                    </tr>
                    <tr>
                      <td className="border-b border-slate-100 px-2 py-2">RPO</td>
                      <td className="border-b border-slate-100 px-2 py-2">2 р.д.</td>
                      <td className="border-b border-slate-100 px-2 py-2">отобранные кандидаты 3–7 р.д.</td>
                      <td className="border-b border-slate-100 px-2 py-2">—</td>
                    </tr>
                    <tr>
                      <td className="px-2 py-2">Расчет ЗП</td>
                      <td className="px-2 py-2">—</td>
                      <td className="px-2 py-2">по графику расчёта/выплат</td>
                      <td className="px-2 py-2">99.5%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-slate-500">Метрики и пороги SLA настраиваются под проект.</p>
            </Card>
          </div>
        </Section>

        <Section gray>
          <h2 className="mb-4 text-2xl font-black">Пакеты</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                name: 'Start',
                items: ['Helpdesk HR + КДП базовое', 'ЭДО и архив', 'Еженед. отчёт'],
                note: 'До 150 чел.',
              },
              {
                name: 'Growth',
                items: ['RPO до 15 вакансий/мес', 'Расчет ЗП/КДП расширенный', 'SLA Premium'],
                note: '150–600 чел.',
              },
              {
                name: 'Enterprise',
                items: ['RPO потоковый + обучение и разработка', 'Интеграции 1С/СКУД/портал', 'Быстрый поиск, панели мониторинга, аудит'],
                safety: true,
                note: '600+ чел.',
              },
            ].map((p) => (
              <Card key={p.name}>
                <h3 className="text-xl font-black">{p.name}</h3>
                <ul className="mt-3 list-disc space-y-2 text-slate-700">
                  {p.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
                <p className="mt-3 text-xs text-slate-500">{p.note}</p>
              </Card>
            ))}
          </div>
        </Section>

        <Section>
          <h2 className="mb-4 text-2xl font-black">Процесс перехода</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6">
            {[
              { n: '1. Исследование', d: 'Интервью, карты процессов, данные.' },
              { n: '2. План', d: 'Целевые схемы, регламенты, RACI.' },
              { n: '3. Переход', d: 'Интеграции, доступы, пилотный проект.' },
              { n: '4. Выполнение', d: 'Операции по SLA, отчётность.' },
              { n: '5. Улучшение', d: 'QBR, аналитика, оптимизации.' },
              { n: '6. Управление', d: 'Контроль рисков и соответствия.' },
            ].map((s) => (
              <Card key={s.n}>
                <h3 className="text-lg font-extrabold">{s.n}</h3>
                <p className="mt-1 text-base text-black">{s.d}</p>
              </Card>
            ))}
          </div>
        </Section>

        <Section gray>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card>
              <h3 className="text-xl font-extrabold">Кейс</h3>
              <p className="mt-2 text-base text-black"><b>Задача:</b> снизить рутину HR и ускорить подбор/документооборот.</p>
              <p className="mt-1 text-base text-black"><b>Решение:</b> RPO + Helpdesk HR + КДП/ЭДО, дашборды и SLA-контур.</p>
              <p className="mt-1 text-base text-black"><b>Результат:</b> первый ответ 20 мин, ≥97% заявок в срок, TtH −30%, экономия 22% HR-OPEX.</p>
            </Card>
            <Card>
              <h3 className="text-xl font-extrabold">FAQ</h3>
              <details className="group mt-2 border-t border-slate-200 py-3" open>
                <summary className="cursor-pointer text-base font-bold">Чем аутсорсинг отличается от аутстаффинга?</summary>
                <p className="mt-1 text-base text-black">
                  Аутсорсинг — передача процесса и результата нам (BPO). Аутстаффинг — вывод сотрудников за штат (или
                  предоставление персонала). Мы разделяем услуги и не используем «заёмный труд».
                </p>
              </details>
              <details className="group border-t border-slate-200 py-3">
                <summary className="cursor-pointer text-base font-bold">Нужна ли аккредитация ЧАЗ?</summary>
                <p className="mt-1 text-base text-black">Для аутсорсинга функций — нет. Аккредитация требуется для предоставления персонала.</p>
              </details>
              <details className="group border-t border-slate-200 py-3">
                <summary className="cursor-pointer text-base font-bold">Как защищены персональные данные?</summary>
                <p className="mt-1 text-base text-black">Ролевые доступы, ЭДО/КЭП, договоры обработки ПДн. По запросу — расширенные меры.</p>
              </details>
            </Card>
          </div>
        </Section>

        <Section id="cta">
          <Card>
            <h3 className="text-xl font-extrabold">Запросить аутсорсинг функций</h3>
            <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs text-slate-500">Имя</span>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                  placeholder="Как к вам обращаться"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-slate-500">Телефон</span>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                  placeholder="+7 () ___--__--__"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs text-slate-500">Процессы</span>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                  placeholder="Напр.: RPO + Payroll + ЭДО"
                  name="processes"
                  value={formData.processes}
                  onChange={handleInputChange}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs text-slate-500">Email</span>
                <input
                  type="email"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                  placeholder="for@example.com"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </label>

              <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={formData.agreed}
                  onChange={(e) => handleAgreementChange(e.target.checked)}
                  className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
                />
                <span>
                  Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
                </span>
              </label>

              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="inline-block rounded-xl px-6 py-3 text-sm font-bold text-white shadow transition-all duration-200 bg-[#FF7C22] hover:brightness-[105%] hover:-translate-y-[1px]"
                >
                  Отправить запрос
                </button>
                {formStatus && <p className={getStatusClasses(formStatus)}>{formStatus}</p>}
              </div>
            </form>
            <p className="mt-2 text-xs text-slate-500">По запросу направим пример SOW и матрицу SLA.</p>
          </Card>
        </Section>
      </main>
    );
  }
